import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ResetPasswordPage.css';

const API = `${process.env.REACT_APP_API_URL || 'https://api.guardianbazaar.com'}/api`;

function getPasswordStrength(pw: string): { score: number; label: string; color: string } {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 10)           score++;
  if (pw.length >= 14)           score++;
  if (/[A-Z]/.test(pw))         score++;
  if (/[0-9]/.test(pw))         score++;
  if (/[^a-zA-Z0-9]/.test(pw)) score++;
  if (score <= 1) return { score, label: 'Weak',       color: '#e74c3c' };
  if (score <= 2) return { score, label: 'Fair',       color: '#e8954a' };
  if (score <= 3) return { score, label: 'Good',       color: '#c8a45a' };
  if (score <= 4) return { score, label: 'Strong',     color: '#2ecc71' };
  return               { score, label: 'Very Strong',  color: '#27ae60' };
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();

  const [email,     setEmail]     = useState('');
  const [code,      setCode]      = useState(['', '', '', '', '', '']);
  const [password,  setPassword]  = useState('');
  const [confirm,   setConfirm]   = useState('');
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [loading,   setLoading]   = useState(false);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const handleCodeChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    if (digit && index < 5) inputs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0)
      inputs.current[index - 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(''));
      inputs.current[5]?.focus();
    }
  };

  const strength = getPasswordStrength(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const fullCode = code.join('');
    if (!email.includes('@'))    { setError('Please enter your email address.'); return; }
    if (fullCode.length !== 6)   { setError('Please enter the 6-digit code.'); return; }
    if (password !== confirm)    { setError('Passwords do not match.'); return; }
    if (strength.score < 3)     { setError('Please choose a stronger password.'); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: fullCode, password }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(data.message);
        setTimeout(() => navigate('/login'), 2500);
      } else {
        setError(data.error || 'Password reset failed.');
        setCode(['', '', '', '', '', '']);
        inputs.current[0]?.focus();
      }
    } catch {
      setError('Could not connect to the server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-page">
      <div className="reset-card">
        <a href="/login">
          <img src="/guardian-bazaar-logo-dark.png" alt="Guardian Bazaar" className="reset-logo" />
        </a>
        <h1 className="reset-title">Reset your password</h1>
        <p className="reset-subtitle">
          Enter your email, the 6-digit code from your email, and your new password.
        </p>

        {success ? (
          <div className="reset-success">
            <div className="reset-success-icon">✓</div>
            <p>{success}</p>
            <p className="reset-success-note">Redirecting to sign in…</p>
          </div>
        ) : (
          <form className="reset-form" onSubmit={handleSubmit}>
            <div className="reset-field">
              <label>Email Address</label>
              <input type="email" value={email}
                     onChange={e => setEmail(e.target.value)}
                     placeholder="your@email.com"
                     autoFocus autoComplete="email" required />
            </div>

            <div className="reset-field">
              <label>6-Digit Reset Code</label>
              <div className="reset-code-row" onPaste={handlePaste}>
                {code.map((digit, i) => (
                  <input key={i}
                    ref={el => { inputs.current[i] = el; }}
                    type="text" inputMode="numeric"
                    maxLength={1} value={digit}
                    onChange={e => handleCodeChange(i, e.target.value)}
                    onKeyDown={e => handleKeyDown(i, e)}
                    className="reset-code-input" />
                ))}
              </div>
            </div>

            <div className="reset-field">
              <label>New Password <span className="reset-hint">min 10 chars, uppercase, number, symbol</span></label>
              <input type="password" value={password}
                     onChange={e => setPassword(e.target.value)}
                     placeholder="Choose a strong password"
                     autoComplete="new-password" required />
              {password && (
                <div className="reset-strength">
                  <div className="reset-strength-bar">
                    {[1,2,3,4,5].map(i => (
                      <div key={i} className="reset-strength-segment"
                           style={{ background: i <= strength.score ? strength.color : 'rgba(255,255,255,0.1)' }} />
                    ))}
                  </div>
                  <span className="reset-strength-label" style={{ color: strength.color }}>{strength.label}</span>
                </div>
              )}
            </div>

            <div className="reset-field">
              <label>Confirm Password</label>
              <div className="reset-input-wrap">
                <input type="password" value={confirm}
                       onChange={e => setConfirm(e.target.value)}
                       placeholder="Repeat your password"
                       autoComplete="new-password" required />
                {confirm && (
                  <span className={`reset-check-icon ${confirm === password ? 'ok' : 'fail'}`}>
                    {confirm === password ? '✓' : '✗'}
                  </span>
                )}
              </div>
            </div>

            {error && <div className="reset-error">{error}</div>}
            <button type="submit" className="reset-btn" disabled={loading}>
              {loading ? 'Updating…' : 'Set New Password'}
            </button>
          </form>
        )}
        <a href="/login" className="reset-back">← Back to sign in</a>
      </div>
    </div>
  );
}
